import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AdminNav from './AdminNav';

const AdminMonitoring = () => {
  const { t } = useTranslation();
  const { getAuthHeaders, logout } = useAuth();
  const [health, setHealth] = useState(null);
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedErrorId, setExpandedErrorId] = useState(null);
  const [levelFilter, setLevelFilter] = useState('');
  const [ackFilter, setAckFilter] = useState('');

  const fetchMonitoring = async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (levelFilter) params.set('level', levelFilter);
      if (ackFilter) params.set('acknowledged', ackFilter);

      const [healthResponse, errorResponse] = await Promise.all([
        fetch('/api/admin/monitoring/health', { headers: getAuthHeaders() }),
        fetch(`/api/admin/monitoring/errors?${params.toString()}`, { headers: getAuthHeaders() })
      ]);

      if (healthResponse.status === 401 || errorResponse.status === 401) {
        logout();
        return;
      }

      const healthData = await healthResponse.json();
      const errorData = await errorResponse.json();

      if (!healthResponse.ok) {
        throw new Error(healthData.error || t('admin.monitoring.failedToLoadHealth'));
      }

      if (!errorResponse.ok) {
        throw new Error(errorData.error || t('admin.monitoring.failedToLoadErrors'));
      }

      setHealth(healthData);
      setErrors(Array.isArray(errorData.errors) ? errorData.errors : []);
    } catch (error) {
      console.error('Monitoring fetch failed:', error);
      setErrorMessage(error.message || t('admin.monitoring.failedToLoadMonitoring'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMonitoring();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelFilter, ackFilter]);

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs}h ${mins}m ${secs}s`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.monitoring.title')}</h1>
              <p className="text-gray-600 mt-2">{t('admin.monitoring.subtitle')}</p>
            </div>
            <button
              onClick={fetchMonitoring}
              disabled={isLoading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw size={16} />
              {isLoading ? t('admin.monitoring.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        <AdminNav />

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6">
            {errorMessage}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-5">
            <p className="text-sm text-gray-500">{t('admin.monitoring.dbStatus')}</p>
            <p className={`text-xl font-semibold ${health?.dbStatus === 'ok' ? 'text-green-600' : 'text-red-600'}`}>
              {health?.dbStatus || 'unknown'}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-5">
            <p className="text-sm text-gray-500">{t('admin.monitoring.uptime')}</p>
            <p className="text-xl font-semibold text-gray-900">
              {health ? formatDuration(health.uptimeSeconds || 0) : '--'}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-5">
            <p className="text-sm text-gray-500">{t('admin.monitoring.errorsLast24h')}</p>
            <p className="text-xl font-semibold text-gray-900">
              {health ? health.errorCountLast24h : '--'}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <AlertTriangle className="text-red-500" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">{t('admin.monitoring.recentErrors')}</h2>
            <div className="flex items-center gap-2 ml-auto">
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">{t('admin.monitoring.allLevels')}</option>
                <option value="error">{t('admin.monitoring.error')}</option>
                <option value="warn">{t('admin.monitoring.warn')}</option>
                <option value="info">{t('admin.monitoring.info')}</option>
              </select>
              <select
                value={ackFilter}
                onChange={(e) => setAckFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">{t('admin.monitoring.all')}</option>
                <option value="false">{t('admin.monitoring.unacknowledged')}</option>
                <option value="true">{t('admin.monitoring.acknowledged')}</option>
              </select>
            </div>
          </div>
          {errors.length === 0 ? (
            <p className="text-sm text-gray-500">{t('admin.monitoring.noErrorsRecorded')}</p>
          ) : (
            <div className="space-y-3">
              {errors.map((err) => (
                <div key={err.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-2">
                    <span className="font-medium text-gray-700">{err.message}</span>
                    {err.statusCode && (
                      <span className="px-2 py-1 bg-gray-100 rounded">{t('admin.monitoring.status', { code: err.statusCode })}</span>
                    )}
                    <span className={`px-2 py-1 rounded ${err.acknowledged ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {err.acknowledged ? t('admin.monitoring.acknowledged') : t('admin.monitoring.unacknowledged')}
                    </span>
                    {err.method && err.route && (
                      <span className="px-2 py-1 bg-gray-100 rounded">
                        {err.method} {err.route}
                      </span>
                    )}
                    <span className="px-2 py-1 bg-gray-100 rounded">
                      {new Date(err.createdAt).toLocaleString()}
                    </span>
                    <button
                      onClick={() => setExpandedErrorId(expandedErrorId === err.id ? null : err.id)}
                      className="ml-auto text-blue-600 hover:text-blue-800 text-xs font-medium"
                    >
                      {expandedErrorId === err.id ? t('admin.monitoring.hideDetails') : t('admin.monitoring.viewDetails')}
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const response = await fetch(`/api/admin/monitoring/errors/${err.id}/acknowledge`, {
                            method: 'PATCH',
                            headers: {
                              ...getAuthHeaders(),
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({ acknowledged: !err.acknowledged })
                          });

                          if (response.status === 401) {
                            logout();
                            return;
                          }

                          const data = await response.json();
                          if (!response.ok) {
                            throw new Error(data.error || t('admin.monitoring.failedToUpdateError'));
                          }

                          setErrors((prev) =>
                            prev.map((item) => (item.id === err.id ? data.error : item))
                          );
                        } catch (updateError) {
                          console.error('Error updating acknowledgment:', updateError);
                          setErrorMessage(updateError.message || t('admin.monitoring.failedToUpdateError'));
                        }
                      }}
                      className="text-xs text-gray-600 hover:text-gray-900"
                    >
                      {err.acknowledged ? t('admin.monitoring.unacknowledge') : t('admin.monitoring.acknowledge')}
                    </button>
                  </div>
                  {expandedErrorId === err.id && (
                    <div className="text-xs text-gray-600 space-y-2">
                      {err.requestId && (
                        <div>
                          <span className="font-semibold">{t('admin.monitoring.requestId')}</span> {err.requestId}
                        </div>
                      )}
                      {err.stack && (
                        <pre className="bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto whitespace-pre-wrap">
                          {err.stack}
                        </pre>
                      )}
                      {err.meta && Object.keys(err.meta).length > 0 && (
                        <pre className="bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto whitespace-pre-wrap">
                          {JSON.stringify(err.meta, null, 2)}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMonitoring;
